import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { defineMessages } from 'react-intl';
import { useParams } from 'react-router-dom';

import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Section } from '@sorare/core/src/components/layout/MainAsideLayout';
import { Warning } from '@sorare/core/src/contexts/intl/Warning';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { metadatas } from '@sorare/core/src/lib/seo/football';

import { SeasonStandings } from 'pages/seo/SeasonStandings';

import type {
  FootballClubStandingsQuery,
  FootballClubStandingsQueryVariables,
} from './__generated__/index.graphql';

const FOOTBALL_CLUB_STANDINGS_QUERY = gql`
  query FootballClubStandingsQuery($slug: String!) {
    football {
      club(slug: $slug) {
        slug
        name
        domesticLeague {
          slug
          seasons {
            id
            startYear
          }
        }
      }
    }
  }
` as TypedDocumentNode<
  FootballClubStandingsQuery,
  FootballClubStandingsQueryVariables
>;

const messages = defineMessages({
  noDomesticLeague: {
    id: 'Seo.Club.Standings.noDomesticLeague',
    defaultMessage: 'This club has no domestic league',
  },
});

export const Standings = () => {
  const { slug } = useParams();
  const { data, loading } = useQuery(FOOTBALL_CLUB_STANDINGS_QUERY, {
    variables: { slug: slug || '' },
    skip: !slug,
  });

  useTitleAndDescription(
    metadatas.clubStandings.title,
    metadatas.clubStandings.description,
    {
      club: data?.football.club.name,
    }
  );

  if (loading) return <LoadingIndicator fullHeight />;
  if (!data) return null;

  const { club } = data.football;

  if (!club.domesticLeague) {
    return (
      <Section topPadding>
        <FullWidthContainer>
          <Warning variant="yellow" title={messages.noDomesticLeague} />
        </FullWidthContainer>
      </Section>
    );
  }

  const blocks = {
    seasonStandings: (
      <SeasonStandings
        displayName={club.name}
        as="h1"
        clubSlug={club.slug}
        competitionSlug={club.domesticLeague.slug}
        seasons={club.domesticLeague.seasons}
      />
    ),
  };

  return <Section topPadding>{blocks.seasonStandings}</Section>;
};
